/* eslint-disable @typescript-eslint/no-unused-vars */
import { Observable, of } from 'rxjs';
import { timeout } from 'rxjs/operators';

import { Injectable } from '@angular/core';
import { Actions, createEffect, Effect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, debounceTime, map, share, switchMap } from 'rxjs/operators';
import { AppState } from '../../interfaces/app-state.interface';
import { BackendService } from '../../services/new-http/backend.service';
import { SuitabilitySantanderAction } from './suitability-santander.action';
import { SuitabilitySantanderState } from './suitability-santander.reducer';
import { SuitabilitySantanderService } from '../../services/suitability-oq/suitability-profile.service';

@Injectable()
export class SuitabilitySantanderEffects {
  /**
   * Lista de perguntas de suitability
   */
  getQuestions$ = createEffect(() => this.actions$.pipe(
    ofType(SuitabilitySantanderAction.getQuestionsSantander),
    switchMap((action: any) => {
      return this.backendService.api
        .post<any>('/oq/v1/questionnaire', action.payload)
        .pipe(
          map((result: SuitabilitySantanderState) => {
            if (result) {
              return {
                type: SuitabilitySantanderAction.getQuestionsSantanderSuccess,
                payload: result
              };
            }

            return {
              type: SuitabilitySantanderAction.getQuestionsSantanderError,
              payload: result
            };
          }),
          catchError(error =>
            of({
              type: SuitabilitySantanderAction.getQuestionsSantanderError,
              payload: error
            })
          )
        );
    }),
    share()
  ));

  /**
   * Salva as respostas no state pra posteriormente enviar.
   */
  setCustomerAnswers$ = createEffect(() => this.actions$.pipe(
    ofType(SuitabilitySantanderAction.setCustomerAnswers),
    map((action: any) => {
      if (action.payload) {
        return {
          type: SuitabilitySantanderAction.setCustomerAnswersSuccess,
          payload: action.payload
        };
      }
      return {
        type: SuitabilitySantanderAction.setCustomerAnswersError,
        payload: action.payload
      };
    }),
    share()
  ));

  /**
   * Envia o questionário respondido.
   */
  sendCustomerAnswers$ = createEffect(() => this.actions$.pipe(
    ofType(SuitabilitySantanderAction.sendCustomerAnswers),
    switchMap((action: any) => {
      return this.backendService.api
        .post<any>('/oq/v1/questionnaire/answer', action.payload)
        .pipe(
          map((result: SuitabilitySantanderState) => {
            if (result) {
              return {
                type: SuitabilitySantanderAction.sendCustomerAnswersSuccess,
                payload: result
              };
            }

            return {
              type: SuitabilitySantanderAction.sendCustomerAnswersError,
              payload: result
            };
          }),
          catchError(error =>
            of({
              type: SuitabilitySantanderAction.sendCustomerAnswersError,
              payload: error
            })
          )
        );
    }),
    share()
  ));

  /**
   * Envia o questionário respondido.
   */
  acceptCustomerAnswers$ = createEffect(() => this.actions$.pipe(
    ofType(SuitabilitySantanderAction.acceptCustomerAnswers),
    switchMap((action: any) => {
      return this.backendService.api
        .post<any>('/oq/v1/questionnaire/accept', action.payload)
        .pipe(
          map((result: SuitabilitySantanderState) => {
            if (result) {
              return {
                type: SuitabilitySantanderAction.acceptCustomerAnswersSuccess,
                payload: result
              };
            }

            return {
              type: SuitabilitySantanderAction.acceptCustomerAnswersError,
              payload: result
            };
          }),
          catchError(error =>
            of({
              type: SuitabilitySantanderAction.acceptCustomerAnswersError,
              payload: error
            })
          )
        );
    }),
    share()
  ));

  /**
   * Busca o perfil de investidor Santander
   */
  getProfile$ = createEffect(() => this.actions$.pipe(
    ofType(SuitabilitySantanderAction.getProfileSantander),
    switchMap(action => {
      return this.backendService.api
      .get<any>('/oq/v1/profile')
        .pipe(
          timeout(15000),
          map((result: SuitabilitySantanderState) => {
            if (result) {
              return {
                type: SuitabilitySantanderAction.getProfileSantanderSuccess,
                payload: result
              };
            }
            return {
              type: SuitabilitySantanderAction.getProfileSantanderError,
              payload: result,
              error: true
            };
          }),
          
          catchError(error =>
            of({
              type: SuitabilitySantanderAction.getProfileSantanderError,
              payload: error,
              error: true
            })
          )
        );
    }),
    share()
  ));

  /**
   * Busca o perfil de investidor Santander
   */
  checkClientExcludeFromCommunication$ = createEffect(() => this.actions$.pipe(
    ofType(SuitabilitySantanderAction.checkClientExcludeFromCommunication),
    switchMap(action => {
      return this.backendService.api
        .get<any>('/oq/v1/communication')
        .pipe(
          map((result: SuitabilitySantanderState) => {
            if (result) {
              return {
                type: SuitabilitySantanderAction.checkClientExcludeFromCommunicationSuccess,
                payload: result
              };
            }
            return {
              type: SuitabilitySantanderAction.checkClientExcludeFromCommunicationError,
              payload: result
            };
          }),
          catchError(error =>
            of({
              type: SuitabilitySantanderAction.checkClientExcludeFromCommunicationError,
              payload: error
            })
          )
        );
    }),
    share()
  ));

  @Effect()
    public generateProfileFittingSuitabilitySantander$: Observable<SuitabilitySantanderAction> = this.actions$
        .pipe(
            ofType(SuitabilitySantanderAction.generateFittingProfileSuitability),
            debounceTime(500),
            switchMap(({ payload }) => {
                return this.suitabilityService.GenerateProfileFitting(payload)
                    .pipe(
                        map((response) => ({
                            type: SuitabilitySantanderAction.generateFittingProfileSuitabilitySuccess,
                            payload: response,
                        })),
                        catchError((error) => {
                            return of({
                                type: SuitabilitySantanderAction.generateFittingProfileSuitabilityError,
                                payload: error,
                            });
                        }));
            }),
        );

    @Effect()
    public acceptProfileFittingSuitabilitySantander$: Observable<SuitabilitySantanderAction> = this.actions$
        .pipe(
            ofType(SuitabilitySantanderAction.acceptFittingProfileSuitability),
            debounceTime(500),
            switchMap(({ payload }) => {
                return this.suitabilityService.AcceptProfileFitting(payload)
                    .pipe(
                        map((response) => ({
                            type: SuitabilitySantanderAction.acceptFittingProfileSuitabilitySuccess,
                            payload: response,
                        })),
                        catchError((error) => {
                            return of({
                                type: SuitabilitySantanderAction.acceptFittingProfileSuitabilityError,
                                payload: error,
                            });
                        }));
            }),
        );

    @Effect()
    public getUserInvestorTypeSuitability$: Observable<SuitabilitySantanderAction> = this.actions$
        .pipe(
            ofType(SuitabilitySantanderAction.getUserInvestorTypeSuitability),
            debounceTime(500),
            switchMap(() => {
                return this.suitabilityService.GetUserInvestorTypeSuitability()
                    .pipe(
                        map((response) => ({
                            type: SuitabilitySantanderAction.getUserInvestorTypeSuitabilitySuccess,
                            payload: response,
                        })),
                        catchError((error) => {
                            return of({
                                type: SuitabilitySantanderAction.getUserInvestorTypeSuitabilityError,
                                payload: error,
                            });
                        }));
            }),
        );


  constructor(
    private backendService: BackendService,
    private store: Store<AppState>,
    private actions$: Actions<SuitabilitySantanderAction>,
    private suitabilityService: SuitabilitySantanderService
  ) {}
}
